import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getActiveTenantId } from "../services/workspace/activeWorkspace";
import { getApiErrorMessage } from "../services/apiErrors";
import { useIsSuperadmin } from "./useIsSuperadmin";
import { fetchAllowedBetaFeatures } from "../services/betaFeatures/betaFeaturesApi";
import { toMemberRows } from "../services/betaFeatures/betaFeaturesMappers";
import {
  BETA_FEATURE_FLAGS,
  BETA_FEATURE_REGISTRY,
} from "../services/betaFeatures/betaFeatureRegistry";
import type { MemberBetaFeatureRow } from "../services/betaFeatures/betaFeatures.types";
import { isFeatureEnabled, setAllowedBetaFlags, setFeatureFlags } from "../utils/featureFlags";

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export const BETA_ALLOWED_QUERY_KEY = "betaFeaturesAllowed";

export const betaAllowedQueryKey = (tenantId: string | null) =>
  [BETA_ALLOWED_QUERY_KEY, tenantId] as const;

/** Developers (superadmins, dev builds) keep today's behaviour: nothing is masked. */
const isDeveloperBuild = (): boolean => process.env.NODE_ENV === "development";

/**
 * Loads the organization's allow-list and masks personal beta overrides the organization has
 * not allowed. Mount once for the signed-in app; the Account panel mounts it too. Flags are
 * read synchronously, so a change applies on the next page load.
 */
export const useApplyBetaFeatureMask = () => {
  const isSuperadmin = useIsSuperadmin();
  const isDeveloper = isSuperadmin || isDeveloperBuild();
  const query = useQuery({
    queryKey: betaAllowedQueryKey(getActiveTenantId()),
    queryFn: fetchAllowedBetaFeatures,
    staleTime: FIVE_MINUTES_MS,
  });
  const allowed = query.data;

  useEffect(() => {
    if (isDeveloper) {
      setAllowedBetaFlags(null);
    } else if (allowed) {
      setAllowedBetaFlags(allowed, BETA_FEATURE_FLAGS);
    }
  }, [isDeveloper, allowed]);

  return { ...query, allowed: allowed ?? [] };
};

export interface UseBetaFeaturesResult {
  rows: MemberBetaFeatureRow[];
  loading: boolean;
  error: string | null;
  /** Saves the member's own choice in this browser. */
  setPersonal: (key: string, on: boolean) => void;
}

/** The beta features the organization allows, each with this member's own on/off. */
export const useBetaFeatures = (): UseBetaFeaturesResult => {
  const { allowed, isLoading, error } = useApplyBetaFeatureMask();
  const [version, setVersion] = useState(0);

  const setPersonal = useCallback((key: string, on: boolean) => {
    const feature = BETA_FEATURE_REGISTRY.find((item) => item.key === key);
    if (!feature) return;
    setFeatureFlags({ [feature.flag]: on });
    setVersion((current) => current + 1);
  }, []);

  // `version` re-reads the flags after a switch; they are not reactive on their own.
  const rows = toMemberRows(BETA_FEATURE_REGISTRY, allowed, isFeatureEnabled);
  void version;

  return {
    rows,
    loading: isLoading,
    error: error ? getApiErrorMessage(error) : null,
    setPersonal,
  };
};
